import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { Body5, Body7, Container } from '@ui';
import { SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { ROUTES } from '@constants/routes';
import { REVIEW_ACTIONS, decideExtractionRows, fetchExtractionRows } from '@services/fmdc';
import { Chip } from '@features/scoreSpec/styles';
import { DangerTransparentButton, HeaderCell, HeaderRow, LinkButton, Mono, Row } from './styles';
import { StatusPill } from './StatusPill';
import { fmdcPaths } from './fmdcUrl';
import { EM_DASH, formatLoadAction, formatReviewerStatus, formatValidationStatus, formatValue } from './format';
import { REVIEWER_STATUS_TOKENS, VALIDATION_TONES, tokenFor } from './statusTokens';

// The cells of one expanded review group: every extraction row of the group's table on that
// spreadsheet row, in sheet order. Each cell links to its value detail, and carries a per-cell
// Approve / Reject that posts a cell-level exception — never carried over to the issuer, and the
// line under the table says so.

// Period | Scenario | Value | As reported | Load action | Validation | Reviewer | actions
const CELL_GRID_COLUMNS =
  'minmax(80px, 0.8fr) minmax(80px, 0.8fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(120px, 1.1fr) minmax(80px, 0.8fr) minmax(100px, 1fr) minmax(230px, 1.6fr)';

const Wrapper = styled.div`
  padding: 8px 8px 12px 36px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// Approve / Reject at cell density: the chip padding (ChipRow's) in place of FlatButton's
// 12px / 24px, so a row keeps its height. The Open link sits outside it and keeps its own zero.
const CellDecisions = styled.div`
  display: flex;
  align-items: center;

  button {
    padding: 6px 14px;
  }
`;

// The row shape is the template's Extraction_Values columns plus ids; the period arrives either
// flattened (`period_label`) or as the payload's object.
export const periodLabelOf = (row) => {
  if (!row) return EM_DASH;
  // Extraction rows carry the template's flat columns; `fiscal_period_label` is the period.
  if (row.fiscal_period_label) return row.fiscal_period_label;
  if (row.period_label) return row.period_label;
  if (row.period && typeof row.period === 'object') return row.period.label || row.period.end_date || EM_DASH;
  if (typeof row.period === 'string') return row.period;
  return EM_DASH;
};

// The table a group belongs to. Two tables can sit side by side on the same rows (a cap table
// in columns B–F, a geography table in T–X), and the pipeline splits them: every extraction row
// carries `table_idx` and the queue groups cells per (sheet, table, row), so one spreadsheet row
// is two groups. Fetching a group's cells by row alone returned the other table's cells under
// this group's label — a side-table "Nordics" showed the cap table's -192.2 first.
//
// `table_idx` is on the review-groups response; the `:tN` suffix on group_id (absent for the
// main table) is the fallback for a response that predates the field. Main table = 0, and 0 is
// always sent, so the main-table group never picks up the side table's cell either.
//
// The field is read as a number or a numeric string: a "1" from a serialiser that quoted it must
// not fall through to the suffix and silently become the main table.
const asTableIndex = (raw) => {
  const idx = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
  return Number.isInteger(idx) && idx >= 0 ? idx : null;
};

export const tableIndexOf = (group) => {
  const own = asTableIndex(group?.table_idx);
  if (own !== null) return own;
  const match = /:t(\d+)$/.exec(group?.group_id || '');
  return match ? Number(match[1]) : 0;
};

// The rows that belong to the group's table. The filter is sent, but the page cannot see whether
// a given server honours it — or reads `table_idx=0` as "not set" — so a row that names another
// table is dropped here too. A row that does not name its table is trusted.
export const ownTableRows = (rows, tableIdx) =>
  (rows || []).filter((row) => {
    const own = asTableIndex(row?.table_idx);
    return own === null || own === tableIdx;
  });

// The row indexes of the ratio rows folded into a group, excluding the parent's own.
export const ratioRowIndexes = (group) => {
  if (!group || !Array.isArray(group.ratio_rows)) return [];
  return group.ratio_rows
    .map((entry) => (entry && typeof entry === 'object' ? entry.row_idx : entry))
    .filter((idx) => idx !== undefined && idx !== null && idx !== group.row_idx);
};

export const GroupCellsTable = ({ runId, group, backTo }) => {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);
  const [decideError, setDecideError] = React.useState(null);

  // A group can fold ratio rows into it (`ratio_rows[]`, each with its own row_idx); the parent's
  // row_idx filter does not return their cells, so each is fetched and the pages are merged.
  const ratioRowIdxs = ratioRowIndexes(group);
  const ratioKey = ratioRowIdxs.join(',');
  // Ratio rows folded into a group belong to the same table as the group.
  const tableIdx = tableIndexOf(group);

  const load = React.useCallback(
    async (guard) => {
      const rowIdxs = [group.row_idx, ...ratioRowIdxs];
      const results = await Promise.all(
        rowIdxs.map((rowIdx) =>
          fetchExtractionRows(runId, { sheet: group.sheet, rowIdx, tableIdx, sort: 'position', limit: 100 })
        )
      );
      if (guard && guard.isCanceled) return;
      const failed = results.find((r) => r.error);
      if (failed) {
        setError(failed.error);
        setRows((prev) => prev || []);
      } else {
        setError(null);
        const fetched = results.flatMap((r) => r.rows);
        setRows(ownTableRows(fetched, tableIdx));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runId, group.sheet, group.row_idx, tableIdx, ratioKey]
  );

  React.useEffect(() => {
    const guard = { isCanceled: false };
    load(guard);
    return () => {
      guard.isCanceled = true;
    };
  }, [load]);

  const decide = async (row, action) => {
    if (busyId) return;
    setBusyId(row.id);
    setDecideError(null);
    const result = await decideExtractionRows({ ids: [row.id], action });
    setBusyId(null);
    if (result.error) {
      setDecideError(result.error);
      return;
    }
    await load(null);
  };

  const openValue = (row) => navigate(fmdcPaths.value(ROUTES.fmdc, runId, row.id), { state: { backTo } });

  // Only for a group with no folded ratio rows: the queue's `cells` counts the parent row alone,
  // so with ratio rows merged in the two numbers are not comparable.
  const countMismatch =
    rows !== null &&
    !error &&
    ratioRowIdxs.length === 0 &&
    Number.isInteger(group.cells) &&
    rows.length !== group.cells;

  if (rows === null && !error) {
    return (
      <Wrapper>
        <Body5 isLowContrast>Loading cells…</Body5>
      </Wrapper>
    );
  }

  if (error && (!rows || rows.length === 0)) {
    return (
      <Wrapper>
        <Body5>{error}</Body5>
        <Container top={8}>
          <SkeletonButton onClick={() => load(null)}>Retry</SkeletonButton>
        </Container>
      </Wrapper>
    );
  }

  return (
    <Wrapper data-testid={`group-cells-${group.group_id}`}>
      <HeaderRow $columns={CELL_GRID_COLUMNS}>
        <HeaderCell>Period</HeaderCell>
        <HeaderCell>Scenario</HeaderCell>
        <HeaderCell>Value</HeaderCell>
        <HeaderCell>As reported</HeaderCell>
        <HeaderCell>Load action</HeaderCell>
        <HeaderCell>Validation</HeaderCell>
        <HeaderCell>Reviewer</HeaderCell>
        <span />
      </HeaderRow>
      {rows.length === 0 && (
        <Container top={16} bottom={8}>
          <Body5 isLowContrast>No cells on this row.</Body5>
        </Container>
      )}
      {rows.map((row) => {
        const value = row.normalized_value !== undefined ? row.normalized_value : row.value;
        const reviewer = row.reviewer_status || 'pending';
        const label = `${periodLabelOf(row)} ${row.scenario || ''}`.trim();
        const busy = busyId === row.id;
        return (
          <Row key={row.id} $columns={CELL_GRID_COLUMNS} $interactive={false}>
            <Body7>{periodLabelOf(row)}</Body7>
            <Body7>{row.scenario || EM_DASH}</Body7>
            <Body7>{formatValue(value, { valueKind: row.value_kind, asReported: row.value_as_reported })}</Body7>
            <Mono>
              {row.value_as_reported === null || row.value_as_reported === undefined
                ? EM_DASH
                : String(row.value_as_reported)}
            </Mono>
            <Body7>{formatLoadAction(row.load_action)}</Body7>
            <div>
              {row.validation_status ? (
                <Chip $tone={VALIDATION_TONES[row.validation_status]}>
                  {formatValidationStatus(row.validation_status)}
                </Chip>
              ) : (
                <Body7 isLowContrast>{EM_DASH}</Body7>
              )}
            </div>
            <div>
              <StatusPill token={tokenFor(REVIEWER_STATUS_TOKENS, reviewer)} label={formatReviewerStatus(reviewer)} />
            </div>
            <Actions>
              <LinkButton onClick={() => openValue(row)} aria-label={`Open ${label}`}>
                Open
              </LinkButton>
              <CellDecisions>
                <TransparentFlatButton
                  onClick={() => decide(row, REVIEW_ACTIONS.APPROVE)}
                  disabled={busy}
                  buttonProps={{ 'aria-label': `Approve this cell only (${label})`, 'aria-disabled': busy }}
                >
                  {busy ? 'Working…' : 'Approve'}
                </TransparentFlatButton>
                <DangerTransparentButton
                  onClick={() => decide(row, REVIEW_ACTIONS.REJECT)}
                  disabled={busy}
                  buttonProps={{ 'aria-label': `Reject this cell only (${label})`, 'aria-disabled': busy }}
                >
                  Reject
                </DangerTransparentButton>
              </CellDecisions>
            </Actions>
          </Row>
        );
      })}
      {decideError && (
        <Container top={8}>
          <ErrorText>{decideError}</ErrorText>
        </Container>
      )}
      {countMismatch && (
        <Container top={8}>
          {/* The queue's count and the fetch disagree: a server that returned the other table's
              cells, or fewer than it counts. Said plainly rather than left for the reader to spot. */}
          <Body7 isLowContrast>
            {rows.length} {rows.length === 1 ? 'cell' : 'cells'} shown; the queue counts {group.cells} for this group.
          </Body7>
        </Container>
      )}
      <Container top={8}>
        <Body7>Decisions here apply to one cell and are not saved for the issuer.</Body7>
      </Container>
    </Wrapper>
  );
};
